import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Experiment } from '@amplitude/experiment-react-native-client';
import fastDeepEqual from 'fast-deep-equal';

import { usePrevious } from 'hooks/use-previous';
import { EventTypes, logEvent } from 'state/amplitude';
import { useCognitoId } from 'state/auth/auth-sync-module';
import AuthStorage from 'utils/cognito/storage';
import { getDeviceIdAsync } from 'utils/device-id';
import { getApiKey, minDevLogLevel } from 'utils/environment';
import { StorageKeys } from 'utils/local-storage';
import logger, { LogLevel } from 'utils/logger';

import { RunningExperiments } from './running-experiments';
import { IExperimentsContext, VariantOptions } from './types';

export { RunningExperiments } from './running-experiments';

const Context = React.createContext<IExperimentsContext>({
  isLoading: false,
  getVariantValue: (_flag: string | RunningExperiments) => undefined,
  logExposure: (_flag: RunningExperiments) => void 0,
  variant: (_flag: RunningExperiments, _expectedVariant?: string, _options?: VariantOptions) =>
    false,
});

export const useExperiments = () => useContext(Context);

const experiment = Experiment.initializeWithAmplitudeAnalytics(
  getApiKey({ key: 'amplitudeExperiments' }),
  {
    debug: __DEV__ && minDevLogLevel() <= LogLevel.debug,
    automaticExposureTracking: false, // we track this manually for many experiments
    automaticFetchOnAmplitudeIdentityChange: true,
    userProvider: {
      getUser: async () => {
        const [deviceId, userId] = await Promise.all([
          getDeviceIdAsync(),
          AuthStorage.getItem(StorageKeys.USER_AUTH_TOKEN),
        ]);

        // we have to lowercase the device id for amplitude to resolve users who do not have a
        // user id yet, but have events with this device id. Our setup currently has
        // a lambda that lowercases the device id, but this request goes straight to amplitude
        // and doesnt get that lambda call.
        const result: any = {
          device_id: deviceId.toLowerCase(),
        };

        if (userId) {
          result.user_id = userId;
        }

        return result;
      },
    },
  }
);
export const experimentsFetchPromise = experiment.fetch();

export const ExperimentsProvider = ({ children }: { children: React.ReactNode }) => {
  const cognitoId = useCognitoId();
  const previousCognitoId = usePrevious(cognitoId);
  const flagExposuresHandled = useRef(new Map<string, boolean>());
  const [isLoading, setIsLoading] = useState(true);
  const [variants, setVariants] = useState(experiment.all()); // from localstorage initially or maybe get lucky and from server if very fast

  const isInitialCognitoEffect = useRef(true);

  useEffect(() => {
    // Clear on sign-out
    if (previousCognitoId && !cognitoId) {
      experiment.clear();
      flagExposuresHandled.current.clear();
      setIsLoading(false);
      return;
    }

    let fetchPromise: Promise<typeof experiment>;
    if (isInitialCognitoEffect.current) {
      // app start fetch initiated outside of react for performance
      fetchPromise = experimentsFetchPromise;
      isInitialCognitoEffect.current = false;
    } else if (cognitoId && cognitoId !== previousCognitoId) {
      // refetch, the user changed
      fetchPromise = experiment.fetch();
    } else {
      // No fetch needed
      return;
    }

    setIsLoading(true);
    fetchPromise
      .then(result => {
        const newVariants = result.all();
        setVariants(prevVariants => {
          const equal = fastDeepEqual(newVariants, prevVariants);
          return equal ? prevVariants : newVariants;
        });
      })
      .catch(error => logger.error({ error, message: 'Amplitude experiments init failure' }))
      .finally(() => setIsLoading(false));
  }, [cognitoId, previousCognitoId]);

  // Override the typical getVariant function to utilize our local cache approach.
  const value = useMemo(() => {
    const logExposure = (
      flagKey: string | RunningExperiments,
      type: 'Manual' | 'Auto' = 'Manual'
    ) => {
      const foundVariant = variants[flagKey];
      const foundValue = foundVariant?.value;
      const flagAndValueKey = flagKey + foundVariant?.value;

      if (!flagExposuresHandled.current.has(flagAndValueKey) && foundValue) {
        logEvent('$exposure', EventTypes.Other, {
          flag_key: flagKey,
          variant: foundValue,
        });
        flagExposuresHandled.current.set(flagAndValueKey, true);
        logger.info(
          `[Experiment] ${type} Logging exposure for "${flagKey}", variant value: "${foundVariant.value}"`
        );
      }
    };

    return {
      isLoading,
      getVariantValue: (flagKey: string | RunningExperiments) => variants[flagKey],
      logExposure,
      variant: (
        flagKey: string,
        expectedVariant: string,
        options = { skipExposureLogging: false }
      ) => {
        if (options.skipExposureLogging !== true) {
          logExposure(flagKey, 'Auto');
        }

        const foundVariant = variants[flagKey];
        return foundVariant?.value === expectedVariant;
      },
    };
  }, [variants, isLoading]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
